import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRoleLabel } from '@/utils/role';
import { workOrderDetailService } from '../services/workOrderDetail.service';
import type { WorkOrderDetailState, WorkOrderDetailViewModel } from '../types/workOrderDetail.types';

const statusLabels: Record<string, string> = {
  assigned: 'Đã phân công',
  completed: 'Hoàn thành',
  closed: 'Hoàn thành',
  in_progress: 'Đang thực hiện',
  paused: 'Tạm dừng',
  overdue: 'Quá hạn',
  draft: 'Nháp',
  not_started: 'Chưa bắt đầu',
  pending: 'Chờ thực hiện',
  cancelled: 'Đã hủy',
};

const priorityLabels: Record<string, string> = {
  high: 'Cao',
  medium: 'Trung bình',
  low: 'Thấp',
  urgent: 'Khẩn cấp',
};

export function useWorkOrderDetail(id: string, companyId?: string) {
  const query = useQuery({
    queryKey: ['construction-work-orders', 'detail', id, companyId],
    queryFn: () => workOrderDetailService.getWorkOrderDetail(id, companyId),
    enabled: Boolean(id),
  });

  const workOrder = useMemo(() => {
    if (!query.data?.order) return null;
    return toViewModel(query.data);
  }, [query.data]);

  return {
    workOrder,
    rawDetail: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

function toViewModel(detail: WorkOrderDetailState): WorkOrderDetailViewModel {
  const order = detail.order || {};
  const progressPercent = order.progress_percent !== undefined && order.progress_percent !== null ? order.progress_percent : 
    (Number(order.planned_quantity) > 0 ? (Number(order.actual_quantity) / Number(order.planned_quantity)) * 100 : 0);
  const progress = Number(progressPercent ?? order.progress ?? 0);
  const status = String(order.status_code || order.status || 'not_started');
  const priority = String(order.priority_code || order.priority || 'medium');
  const title = pickText(order.title, order.name, order.work_order_name, order.work_order_code, 'Chưa có tiêu đề');

  return {
    id: String(order.id || ''),
    code: pickText(order.work_order_code, order.code, '---'),
    title,
    description: pickText(order.description, detail.boqItem?.description, 'Chưa có mô tả cho lệnh thi công này.'),
    status,
    statusLabel: pickText(statusLabels[status], order.status_label, status),
    priority,
    priorityLabel: pickText(priorityLabels[priority], order.priority_label, priority),
    workTypeLabel: order.work_type === 'maintenance' ? 'Bảo dưỡng' : 'Thi công',
    projectId: String(order.project_id || ''),
    companyId: String(order.company_id || ''),
    projectName: pickText(detail.project?.name, order.project_name, 'Chưa có dự án'),
    zoneName: pickText(detail.zone?.zone_name, detail.zone?.zone_code, order.location, 'Chưa có khu vực'),
    phaseName: pickText(detail.phase?.name, detail.phase?.code, 'Chưa có giai đoạn'),
    boqName: pickText(detail.boqItem?.item_name, detail.boqItem?.item_code, 'Chưa liên kết BOQ'),
    teamName: pickText(detail.team?.name, detail.team?.code, 'Chưa phân công đội'),
    leadName: pickText(detail.lead?.full_name, order.lead_name, 'Chưa phân công'),
    leadEmail: pickText(detail.lead?.email, order.lead_email, ''),
    leadAvatar: detail.lead?.avatar_path || null,
    leadRoleName: getRoleLabel(detail.lead?.role_code || ''),
    leadRoleCode: detail.lead?.role_code || '',
    progress: Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : 0,
    plannedStart: order.planned_start_at || order.scheduled_date || order.start_date || null,
    plannedEnd: order.planned_end_at || order.due_date || order.end_date || null,
    actualStart: order.started_at || null,
    actualEnd: order.completed_at || null,
    dueDate: order.due_date || order.planned_end_at || null,
    estimatedCost: Number(order.estimated_cost ?? order.budget_amount ?? 0),
    actualCost: Number(order.actual_cost ?? 0),
    area_m2: order.area_m2 ?? null,
    tree_count: order.tree_count ?? null,
    lawn_area_m2: order.lawn_area_m2 ?? null,
    shrub_area_m2: order.shrub_area_m2 ?? null,
    hedge_length_m: order.hedge_length_m ?? null,
    material_quantity: order.material_quantity ?? null,
    scheduled_date: order.scheduled_date || order.planned_start_at || null,
    createdAt: order.created_at || null,
    updatedAt: order.updated_at || null,
    tasks: detail.tasks,
    members: detail.members,
    materials: (detail.materials || []).map(m => ({
      id: String(m.id || ''),
      material_name: String(m.material_name || 'N/A'),
      planned_qty: Number(m.planned_qty || 0),
      issued_qty: Number(m.issued_qty || 0),
      used_qty: Number(m.used_qty || 0),
      waste_qty: Number(m.waste_qty || 0),
      unit: String(m.unit || 'lot'),
      status_code: String(m.status_code || 'assigned')
    })),
    equipment: (detail.equipment || []).map(e => ({
      id: String(e.id || ''),
      equipment_name: String(e.equipment_name || 'N/A'),
      planned_hours: Number(e.planned_hours || 0),
      actual_hours: Number(e.actual_hours || 0),
      status_code: String(e.status_code || 'assigned')
    })),
    checklists: detail.checklists,
    photos: detail.photos,
    logs: detail.logs,
    inspections: detail.inspections,
  };
}

function pickText(...values: Array<unknown>) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number') return String(value);
  }
  return '';
}
